import { isNotNil } from 'ramda';
import { useEffect, useState } from 'react';

import {
  ContractDemand,
  ContractDemandSourcingCase,
  ContractDemandSourcingCaseQuoteItem,
} from './ContractDemandAwardingWidget.types';

export interface LineItemSourcingCaseAssignment {
  [lineItemId: string]: string | undefined;
}

const getAssignableQuoteItemsBySourcingCase = (sourcingCase: ContractDemandSourcingCase) => {
  return (
    sourcingCase.quote?.lineItems.filter((quoteItem) => {
      return quoteItem.isAwardable;
    }) ?? []
  );
};

export interface LineItemSourcingCaseAssignments {
  value: LineItemSourcingCaseAssignment | undefined;
  toggleLineItemAssignment: (lineItemId: string, sourcingCase: ContractDemandSourcingCase) => void;
  assignAllLineItemsToSourcingCase: (sourcingCase: ContractDemandSourcingCase) => void;
  areAllLineItemsAssignedToSourcingCase: (sourcingCase: ContractDemandSourcingCase) => boolean;
  isLineItemAssignedToSourcingCase: (lineItemId: string, sourcingCase: ContractDemandSourcingCase) => boolean;
  isAtLeastOneLineItemAssigned: boolean;
  hasAssignableQuoteItems: boolean;
  hasAssignableQuoteItemsForSourcingCase: (sourcingCase: ContractDemandSourcingCase) => boolean;
  isAwardingActiveForSourcingCase: (sourcingCase: ContractDemandSourcingCase) => boolean;
  hasBestOfferForQuoteItem: (lineItemId: string) => boolean;
}

/**
 * A hook that provides state-handling and helper methods for working with the line item assignments during awarding
 */
export const useLineItemAssignments = (contractDemand: ContractDemand | undefined): LineItemSourcingCaseAssignments => {
  const [value, setValue] = useState<LineItemSourcingCaseAssignment | undefined>();
  const { demandRequest, sourcingCases, bestOfferQuoteItems } = contractDemand || {};
  const sourcingCasesNormalized = sourcingCases || [];

  useEffect(() => {
    if (demandRequest?.lineItems) {
      const emptySelectionState = (demandRequest.lineItems || []).reduce((acc, lineItem) => {
        acc[lineItem.id] = undefined;
        return acc;
      }, {} as LineItemSourcingCaseAssignment);
      setValue(emptySelectionState);
    }
  }, [demandRequest]);

  const assignableQuoteItemsBySourcingCase: Record<string, ContractDemandSourcingCaseQuoteItem[]> = {};
  sourcingCasesNormalized.forEach((sourcingCase) => {
    assignableQuoteItemsBySourcingCase[sourcingCase.id] = getAssignableQuoteItemsBySourcingCase(sourcingCase);
  });

  // Provide a null-object if data is not ready or state has not been initialized yet
  if (!value || sourcingCasesNormalized.length === 0) {
    return {
      value,
      toggleLineItemAssignment: () => {},
      assignAllLineItemsToSourcingCase: () => {},
      areAllLineItemsAssignedToSourcingCase: () => false,
      isLineItemAssignedToSourcingCase: () => false,
      isAtLeastOneLineItemAssigned: false,
      hasAssignableQuoteItems: false,
      hasAssignableQuoteItemsForSourcingCase: () => false,
      isAwardingActiveForSourcingCase: () => false,
      hasBestOfferForQuoteItem: () => false,
    };
  }

  return {
    value,
    toggleLineItemAssignment: (lineItemId: string, sourcingCase: ContractDemandSourcingCase) => {
      if (value[lineItemId] === sourcingCase.id) {
        // Deselecting the line item
        setValue((prev) => ({
          ...prev,
          [lineItemId]: undefined,
        }));
        return;
      }

      // Selecting the line item for the given sourcing case
      setValue((prev) => ({
        ...prev,
        [lineItemId]: sourcingCase.id,
      }));
    },
    assignAllLineItemsToSourcingCase: (sourcingCase: ContractDemandSourcingCase) => {
      const selectableQuoteItems = assignableQuoteItemsBySourcingCase[sourcingCase.id];
      const newSelectionState = selectableQuoteItems.reduce((acc, quoteItem) => {
        acc[quoteItem.demandRequestItemId] = sourcingCase.id;
        return acc;
      }, {} as LineItemSourcingCaseAssignment);
      setValue({ ...value, ...newSelectionState });
    },

    areAllLineItemsAssignedToSourcingCase: (sourcingCase: ContractDemandSourcingCase) => {
      const selectableQuoteItems = assignableQuoteItemsBySourcingCase[sourcingCase.id];

      return (
        selectableQuoteItems.length > 0 &&
        selectableQuoteItems.every((quoteItem) => value[quoteItem.demandRequestItemId] === sourcingCase.id)
      );
    },

    isLineItemAssignedToSourcingCase: (lineItemId: string, sourcingCase: ContractDemandSourcingCase) => {
      return value[lineItemId] === sourcingCase.id;
    },

    isAtLeastOneLineItemAssigned: (() => {
      // Get each line item, that has at least one selectable quote item
      const selectableLineItems = Object.keys(value).filter((lineItemId) => {
        for (const sourcingCase of sourcingCasesNormalized) {
          const foundInCase = assignableQuoteItemsBySourcingCase[sourcingCase.id].some(
            (quoteItem) => quoteItem.demandRequestItemId === lineItemId,
          );

          if (foundInCase) {
            return true;
          }
        }

        return false;
      });

      // Validated, that at least one of those line items is assigned to a sourcing case
      return (
        selectableLineItems.length > 0 && selectableLineItems.some((lineItemId) => value[lineItemId] !== undefined)
      );
    })(),

    hasAssignableQuoteItems: (() => {
      return (
        sourcingCasesNormalized.length > 0 &&
        sourcingCasesNormalized.some((sourcingCase) => assignableQuoteItemsBySourcingCase[sourcingCase.id].length > 0)
      );
    })(),

    hasAssignableQuoteItemsForSourcingCase: (sourcingCase: ContractDemandSourcingCase) => {
      return assignableQuoteItemsBySourcingCase[sourcingCase.id].length > 0;
    },

    isAwardingActiveForSourcingCase: (sourcingCase: ContractDemandSourcingCase) => {
      return sourcingCase.isAwardable;
    },

    hasBestOfferForQuoteItem: (lineItemId: string) => {
      return isNotNil(bestOfferQuoteItems) && bestOfferQuoteItems.some((item) => item.id === lineItemId);
    },
  };
};
